import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Category, TagWithCount } from '@/types'

import Link from 'next/link'
import { Compass } from 'lucide-react'
import { getRequestOrigin } from '@/lib/request'
import { getCategories, getPopularTags } from '@/lib/blog'
import { countLabel } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { EmptyState } from '@/components/blog/empty-state'
import { LoadError } from '@/components/blog/load-error'
import { PageHeader } from '@/components/blog/page-header'
import { SectionHeading } from '@/components/blog/section-heading'
import { TopicCard } from '@/components/blog/topic-card'

interface TopicsProps {
    categories: Category[]
    tags: TagWithCount[]
    url: string
    failed: boolean
}

const Topics: FC<TopicsProps> = ({ categories, tags, url, failed }): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Topics'
                description='Browse the journal by topic: design, travel, food and culture, plus every tag our writers use.'
                url={url || undefined}
            />

            <PageHeader
                eyebrow='Topics'
                title='Explore by topic'
                description='Every story belongs to one topic. Pick a thread and follow it, or browse the tags below to find stories that cross between them.'
                meta={failed ? undefined : countLabel(categories.length, 'topic', 'topics')}
            />

            <Container className='pt-14 sm:pt-16'>
                {failed && <LoadError />}

                {!failed && categories.length === 0 && (
                    <EmptyState
                        icon={Compass}
                        title='No topics yet'
                        description='Topics will appear here as soon as the first stories are filed under them.'
                        action={{ label: 'Browse all stories', href: '/blog' }}
                    />
                )}

                {!failed && categories.length > 0 && (
                    <>
                        <h2 className='sr-only'>
                            All topics
                        </h2>

                        <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
                            {categories.map(category => (
                                <TopicCard key={category.id} category={category} />
                            ))}
                        </div>
                    </>
                )}

                {!failed && tags.length > 0 && (
                    <section id='tags' aria-labelledby='tags-heading' className='mt-20 sm:mt-24'>
                        <SectionHeading
                            id='tags-heading'
                            eyebrow='Tags'
                            title='Threads that run through the journal'
                        />

                        <ul className='mt-10 flex flex-wrap gap-3'>
                            {tags.map(tag => (
                                <li key={tag.id}>
                                    <Link
                                        href={`/tags/${tag.slug}`}
                                        className='group inline-flex h-11 items-center gap-2 rounded-full border border-border bg-card pl-4 pr-3 text-[0.95rem] text-foreground transition-colors hover:border-foreground/30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                                    >
                                        <span aria-hidden='true' className='text-brand'>#</span>
                                        <span>{tag.name}</span>

                                        <span className='inline-flex min-w-6 items-center justify-center rounded-full bg-accent px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground'>
                                            {tag.postCount}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </Container>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<TopicsProps> = async ({ req, res }) => {
    const origin = getRequestOrigin(req)
    const url = origin ? `${origin}/topics` : ''

    try {
        const [categories, tags] = await Promise.all([
            getCategories(),
            getPopularTags(40)
        ])

        return { props: { categories, tags, url, failed: false } }
    }
    catch (error) {
        console.error('Could not load the topics page:', error)
        res.statusCode = 503

        return { props: { categories: [], tags: [], url, failed: true } }
    }
}

export default Topics
